/**
 * SOLID Review
 * S — Strong. One concern: the current audio output route (headphones/speakers/unknown) while a call is up, and the manual mute
 *     override — classification and transitions are `classifyAudioRoute`/`audioRouteTransitions`'.
 * O — Good. New route types or transition rules live in those two modules.
 * L — Strong. A view model (`IServiceViewModel`), whose reactions another controller can declare among its own (`reactionsOver`).
 * I — Strong. IServiceAudioRoute: the route, its label, the mute, the notice, one setter.
 * D — Strong. The media devices and timers it is handed; no `navigator` of its own.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions, type IConfigReaction } from './Reactions';
import type { IServiceMediaDevices, IServiceViewTimers, TViewTimer } from './platform/IConfigDepViewPlatform';
import { classifyAudioRoute, type AudioRoute } from '../audio/classifyAudioRoute';
import {
  applyManualMute, applyRouteChange, clearNotice, initialAudioRouteState, type AudioNotice, type IStateAudioRoute,
} from '../audio/audioRouteTransitions';

/** Bluetooth connects fire several devicechange events (one per profile); collapse them. */
const DEVICECHANGE_DEBOUNCE_MS = 300;
/** How long the "headphones connected" confirmation stays up. */
const CONNECTED_NOTICE_MS = 4000;

export interface IServiceAudioRoute {
  readonly route: AudioRoute;
  readonly matchedLabel?: string;
  readonly remoteAudioMuted: boolean;
  readonly notice: AudioNotice;
  /** Manual override entry point — survives until the next physical route transition. */
  setRemoteAudioMuted(muted: boolean): void;
}

interface IStateAudioMonitor {
  readonly route: IStateAudioRoute;
  readonly matchedLabel: string | undefined;
}

/**
 * THE AUDIO ROUTE MONITOR (frontend-framework-oo P2, 2026-10-29, §28.294) — `hooks/useAudioRouteMonitor.ts` as a controller: while a
 * LiveKit session is up it watches the device's audio route, so the UI can announce plug/unplug transitions and auto-mute incoming
 * audio on speakers (echo). Detection is the media devices' list and their `devicechange`, debounced; the "connected" notice
 * dismisses itself; the session's end resets everything. Its three effects are three reactions in the hook's order —
 * `reactionsOver` hands them to a controller that declares them among its own (the LiveKit session's).
 */
export class AudioRouteMonitor implements IServiceViewModel<IServiceAudioRoute> {
  private readonly state = new ObservableState<IStateAudioMonitor>(AudioRouteMonitor.unclassified());
  private readonly reactions: Reactions<{ readonly active: boolean; readonly notice: AudioNotice }>;
  private shown: { readonly from: IStateAudioMonitor; readonly route: IServiceAudioRoute } | null = null;

  constructor(
    private readonly devices: IServiceMediaDevices,
    private readonly timers: IServiceViewTimers,
  ) {
    this.reactions = new Reactions(this.reactionsOver((facts) => facts.active, (facts) => facts.notice));
  }

  /** Nothing classified yet: the route unknown, no label. */
  private static unclassified(): IStateAudioMonitor {
    const fresh: IStateAudioMonitor = { route: initialAudioRouteState(), matchedLabel: undefined };
    return fresh;
  }

  readonly setRemoteAudioMuted = (muted: boolean): void => {
    const change: Partial<IStateAudioMonitor> = { route: applyManualMute(this.state.get().route, muted) };
    this.state.update(change);
  };

  /** The hook's three effects, as reactions over whatever facts the declaring controller has: whether a call is up, the notice. */
  reactionsOver<TFacts>(activeOf: (facts: TFacts) => boolean, noticeOf: (facts: TFacts) => AudioNotice): Array<IConfigReaction<TFacts>> {
    const reset: IConfigReaction<TFacts> = {
      deps: (facts) => [activeOf(facts)],
      // The session ended: the next starts fresh (no stale notice, override or mute).
      run: (facts) => {
        if (activeOf(facts)) return;
        const fresh: Partial<IStateAudioMonitor> = { route: initialAudioRouteState(), matchedLabel: undefined };
        this.state.update(fresh);
      },
    };
    const detect: IConfigReaction<TFacts> = {
      deps: (facts) => [activeOf(facts)],
      run: (facts) => {
        if (!activeOf(facts) || !this.devices.available()) return undefined;
        let disposed = false;
        let debounceTimer: TViewTimer | null = null;
        const classify = async (): Promise<void> => {
          try {
            const listed = await this.devices.list();
            if (disposed) return;
            const classification = classifyAudioRoute(listed);
            const detected: Partial<IStateAudioMonitor> = {
              matchedLabel: classification.matchedLabel, route: applyRouteChange(this.state.get().route, classification),
            };
            this.state.update(detected);
          } catch {
            /* permission denied or API unavailable — stay in current state */
          }
        };
        void classify();
        const leave = this.devices.onChange(() => {
          if (debounceTimer !== null) this.timers.clearTimeout(debounceTimer);
          debounceTimer = this.timers.setTimeout(() => {
            debounceTimer = null;
            void classify();
          }, DEVICECHANGE_DEBOUNCE_MS);
        });
        return () => {
          disposed = true;
          if (debounceTimer !== null) this.timers.clearTimeout(debounceTimer);
          leave();
        };
      },
    };
    const dismiss: IConfigReaction<TFacts> = {
      deps: (facts) => [noticeOf(facts)],
      // The connected confirmation dismisses itself; the disconnected banner persists.
      run: (facts) => {
        if (noticeOf(facts) !== 'connected') return undefined;
        const timer = this.timers.setTimeout(() => {
          const cleared: Partial<IStateAudioMonitor> = { route: clearNotice(this.state.get().route) };
          this.state.update(cleared);
        }, CONNECTED_NOTICE_MS);
        return () => this.timers.clearTimeout(timer);
      },
    };
    return [reset, detect, dismiss];
  }

  /** The route as a view reads it — the same object until the route or its label changes. */
  getSnapshot(): IServiceAudioRoute {
    const from = this.state.get();
    if (this.shown === null || this.shown.from !== from) {
      const route: IServiceAudioRoute = {
        route: from.route.route,
        matchedLabel: from.matchedLabel,
        remoteAudioMuted: from.route.muted,
        notice: from.route.notice,
        setRemoteAudioMuted: this.setRemoteAudioMuted,
      };
      this.shown = { from, route };
    }
    return this.shown.route;
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(inputs: readonly unknown[], rendered: IServiceAudioRoute): void {
    this.reactions.stopChanged(AudioRouteMonitor.factsOf(inputs, rendered));
  }

  commit(inputs: readonly unknown[], rendered: IServiceAudioRoute): void {
    this.reactions.startChanged(AudioRouteMonitor.factsOf(inputs, rendered));
  }

  private static factsOf(inputs: readonly unknown[], rendered: IServiceAudioRoute): { readonly active: boolean; readonly notice: AudioNotice } {
    const [active] = inputs as readonly [boolean];
    return { active, notice: rendered.notice };
  }
}
